/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.StateFlow
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.datastore.UserPreferencesRepository
import studio.oddpods.cauce.core.model.DarkThemeConfig
import studio.oddpods.cauce.core.model.LanguageConfig
import studio.oddpods.cauce.core.model.ThemeBrand
import studio.oddpods.cauce.core.model.UserData
import studio.oddpods.cauce.core.model.display.DisplayPreferences

class UserDataRepositoryImpl(
    private val preferencesRepository: UserPreferencesRepository,
) : UserDataRepository {
    override val userData: StateFlow<UserData>
        get() = preferencesRepository.userData

    override val authToken: String?
        get() = preferencesRepository.authToken

    override val passcode: String
        get() = preferencesRepository.passcode

    override val observeLanguage: Flow<LanguageConfig>
        get() = preferencesRepository.observeLanguage

    override val observeDarkThemeConfig: Flow<DarkThemeConfig>
        get() = preferencesRepository.observeDarkThemeConfig

    override val observeDynamicColorPreference: Flow<Boolean>
        get() = preferencesRepository.observeDynamicColorPreference

    override val observeScreenCapturePreference: Flow<Boolean>
        get() = preferencesRepository.observeScreenCapturePreference

    override val observeOnboardingBannerDismissed: Flow<Boolean>
        get() = preferencesRepository.observeOnboardingBannerDismissed

    override val observeDisplayPreferences: Flow<DisplayPreferences>
        get() = preferencesRepository.observeDisplayPreferences

    override suspend fun setLanguage(language: LanguageConfig) = preferencesRepository.setLanguage(language)

    override suspend fun setThemeBrand(themeBrand: ThemeBrand) = preferencesRepository.setThemeBrand(themeBrand)

    override suspend fun setDarkThemeConfig(darkThemeConfig: DarkThemeConfig) =
        preferencesRepository.setDarkThemeConfig(darkThemeConfig)

    override suspend fun setDynamicColorPreference(useDynamicColor: Boolean) =
        preferencesRepository.setDynamicColorPreference(useDynamicColor)

    override suspend fun setIsAuthenticated(isAuthenticated: Boolean) =
        preferencesRepository.setIsAuthenticated(isAuthenticated)

    override suspend fun setIsUnlocked(isUnlocked: Boolean) = preferencesRepository.setIsUnlocked(isUnlocked)

    override suspend fun setIsPasscodeEnabled(isPasscodeEnabled: Boolean) =
        preferencesRepository.setIsPasscodeEnabled(isPasscodeEnabled)

    override suspend fun setIsBiometricsEnabled(isBiometricsEnabled: Boolean) =
        preferencesRepository.setIsBiometricsEnabled(isBiometricsEnabled)

    override suspend fun setShowOnboarding(showOnboarding: Boolean) =
        preferencesRepository.setShowOnboarding(showOnboarding)

    override suspend fun setFirstTimeState(firstTimeState: Boolean) =
        preferencesRepository.setFirstTimeState(firstTimeState)

    override suspend fun setPasscode(passcode: String) = preferencesRepository.setPasscode(passcode)

    override suspend fun setOnboardingBannerDismissed(dismissed: Boolean) =
        preferencesRepository.setOnboardingBannerDismissed(dismissed)

    override suspend fun setDisplayPreferences(prefs: DisplayPreferences) =
        preferencesRepository.setDisplayPreferences(prefs)

    override suspend fun clearUserData() = preferencesRepository.clearUserData()
}
